import {
  Area,
  Avatar,
  Box,
  Divider,
  Typography,
  Icons,
  IconButton,
  Tooltip,
  Modal,
  Button,
} from '@ublend-npm/aulaui-core';
import React, { useState } from 'react';
import HandinLogo from '../../../../../../static/handin-logo.svg';
import SSALogo from '../../../../../../static/ssa-logo.png';
import { ASSIGNMENT_TOOLS } from '@app/constants/texts';

interface AssignmentToolsProps {
  baseDomain: string;
  spaceId: string;
  styles: any;
  isEducator: boolean;
  isDesktop: boolean;
}

const AssignmentTools = ({
  baseDomain,
  spaceId,
  styles,
  isEducator,
  isDesktop,
}: AssignmentToolsProps) => {
  // LTI tools to be displayed in the assignment tools section
  const LTI_TOOLS: Record<string, any> = {
    ssa: {
      id: 'ssa',
      title: 'Student success app',
      description: {
        educator:
          'Understand how students are progressing, view all assignments and grades in one place and pass back grades to the student record system.',
        student:
          'View all your assignments and grades in one place, understand how assignments contribute to your final grade and keep track of how you’re progressing in your learning journey.',
      },
      url: `https://success.${baseDomain}/${spaceId}`,
      openInNewTab: true,
      logo: SSALogo,
    },
    handin: {
      id: 'handin',
      title: 'Handin',
      description: {
        educator:
          'Aula’s assessment tool: here you can create and manage Handin assignments, grade submissions and provide feedback to students.',
        student:
          'Aula’s assessment tool: here you can view upcoming, current and past Handin assignments, submit your work and view grades and feedback.',
      },
      url: `https://handin.${baseDomain}/${spaceId}`,
      openInNewTab: true,
      logo: HandinLogo,
    },
  };
  // State to store the ID of the tool to be displayed in the info modal
  const [infoToolModalId, setInfoToolModalId] = useState<
    keyof typeof LTI_TOOLS | null
  >(null);

  const handleOpenInfoModal = (toolId: string) => {
    setInfoToolModalId(toolId);
  };

  const handleCloseInfoModal = () => {
    setInfoToolModalId(null);
  };

  const InfoIcon = (tool: any) => (
    <IconButton
      aria-label={
        isEducator ? tool.description.educator : tool.description.student
      }
      sx={{ ml: '-0.4rem' }}
      onClick={() => handleOpenInfoModal(tool?.id)}
    >
      <Tooltip
        disableInteractive
        disableFocusListener={!isDesktop}
        disableHoverListener={!isDesktop}
        disableTouchListener={!isDesktop}
        position="top"
        title={
          isEducator ? tool.description.educator : tool.description.student
        }
        slotProps={{ ...styles.tooltipStyles }}
      >
        <Icons.InfoOutlined />
      </Tooltip>
    </IconButton>
  );

  const InfoModal = !isDesktop && infoToolModalId !== null && (
    <Modal open onClose={handleCloseInfoModal}>
      <Area rounded elevated sx={styles.modalContainer}>
        <Area sx={styles.modalHeader}>
          <Typography variant="h6" component="h1">
            {ASSIGNMENT_TOOLS}
          </Typography>
          <IconButton aria-label="Close" onClick={handleCloseInfoModal}>
            <Icons.Close />
          </IconButton>
        </Area>
        <Area sx={styles.modalContent}>
          <Area sx={{ p: 0 }}>
            <Typography sx={styles.modalCardTitle}>
              {LTI_TOOLS?.[infoToolModalId]?.title}
            </Typography>
            <Typography sx={styles.modalCardDescription}>
              {isEducator
                ? LTI_TOOLS?.[infoToolModalId]?.description?.educator
                : LTI_TOOLS?.[infoToolModalId]?.description?.student}
            </Typography>
          </Area>
          <Area sx={styles.modalFooter}>
            <Button aria-label="Close" onClick={handleCloseInfoModal}>
              Close
            </Button>
          </Area>
        </Area>
      </Area>
    </Modal>
  );

  return (
    <Area sx={styles.assignmentToolsWrapper}>
      {Object.values(LTI_TOOLS)?.map((tool, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              ...styles.common,
              justifyContent: 'space-between',
              height: '2rem',
            }}
          >
            <Box
              sx={{
                ...styles.common,
                gap: '0.5rem',
              }}
            >
              <Avatar
                sx={{ borderRadius: '0.5rem', ...styles.avatarStyle }}
                src={tool.logo}
                fallbackText={tool.title}
                altText={tool.title}
              />
              <Typography sx={styles.toolTitle}>{tool.title}</Typography>
              {InfoIcon(tool)}
            </Box>
            <IconButton
              aria-label="Open in new tab"
              onClick={() =>
                window.open(tool.url, '_blank', 'noopener,noreferrer')
              }
            >
              <Tooltip
                disableInteractive
                disableFocusListener={!isDesktop}
                disableHoverListener={!isDesktop}
                disableTouchListener={!isDesktop}
                position="top"
                title="Open in new tab"
              >
                <Icons.OpenInNew sx={styles.newTabIcon} />
              </Tooltip>
            </IconButton>
            {InfoModal}
          </Box>
          {index < Object.values(LTI_TOOLS)?.length - 1 && (
            <Divider sx={{ my: '0.5rem' }} />
          )}
        </React.Fragment>
      ))}
    </Area>
  );
};

export default AssignmentTools;
